// La mitad derecha del cuadro «Cómo originamos» (C5): UNA TIENDA POR PUNTO.
//
// X = SCI promedio · Y = cosecha de 1ª cuota a 14d · radio = volumen SureKeep · color = captura.
// El plano se parte en los cuatro cuadrantes del mockup con los cortes SCI 60 y cosecha 60 %. El
// `quadrant` de cada tienda lo resuelve el silo: acá se dibujan las zonas, no se vuelve a clasificar.
import type { StoreCohortPoint } from '@/api/services/retail-wizard/backoffice';
import { formatPct, formatSci, formatUnits } from '../lib/originationMetrics';
import {
    captureColor,
    maxSureKeepUnits,
    plottableStorePoints,
    quadrantLabel,
    quadrantMeaning,
    scatterRadius,
    scatterStoreLabel,
    SCATTER_SCI_MAX,
    SCATTER_SCI_MIN,
    STORE_QUADRANT_COHORT_CUT_PCT,
    STORE_QUADRANT_SCI_CUT,
} from '../lib/originationCohort';

// Las medidas del mock, tal cual.
const WIDTH = 800;
const HEIGHT = 260;
const PAD_LEFT = 42;
const PAD_RIGHT = 14;
const PAD_TOP = 12;
const PAD_BOTTOM = 38;

const INNER_WIDTH = WIDTH - PAD_LEFT - PAD_RIGHT;
const INNER_HEIGHT = HEIGHT - PAD_TOP - PAD_BOTTOM;

const xPos = (sci: number) =>
    PAD_LEFT +
    ((Math.min(SCATTER_SCI_MAX, Math.max(SCATTER_SCI_MIN, sci)) - SCATTER_SCI_MIN) /
        (SCATTER_SCI_MAX - SCATTER_SCI_MIN)) *
        INNER_WIDTH;

const yPos = (cohortPct: number) =>
    PAD_TOP + (1 - Math.min(100, Math.max(0, cohortPct)) / 100) * INNER_HEIGHT;

const X_CUT = xPos(STORE_QUADRANT_SCI_CUT);
const Y_CUT = yPos(STORE_QUADRANT_COHORT_CUT_PCT);

/** Las cuatro zonas con el color y la esquina que el mock les da. */
const QUADRANT_ZONES = [
    {
        quadrant: 'LUCKY',
        fill: 'hsl(var(--warning-bg))',
        text: 'hsl(var(--warning))',
        x: PAD_LEFT,
        y: PAD_TOP,
        width: X_CUT - PAD_LEFT,
        height: Y_CUT - PAD_TOP,
        labelX: PAD_LEFT + 8,
        labelY: PAD_TOP + 14,
        anchor: 'start',
    },
    {
        quadrant: 'MODEL',
        fill: 'hsl(var(--success-bg))',
        text: 'hsl(var(--success))',
        x: X_CUT,
        y: PAD_TOP,
        width: PAD_LEFT + INNER_WIDTH - X_CUT,
        height: Y_CUT - PAD_TOP,
        labelX: PAD_LEFT + INNER_WIDTH - 8,
        labelY: PAD_TOP + 14,
        anchor: 'end',
    },
    {
        quadrant: 'PROBLEM',
        fill: 'hsl(var(--danger-bg))',
        text: 'hsl(var(--danger))',
        x: PAD_LEFT,
        y: Y_CUT,
        width: X_CUT - PAD_LEFT,
        height: PAD_TOP + INNER_HEIGHT - Y_CUT,
        labelX: PAD_LEFT + 8,
        labelY: PAD_TOP + INNER_HEIGHT - 6,
        anchor: 'start',
    },
    {
        quadrant: 'SOMETHING_OFF',
        fill: 'hsl(var(--warning-bg))',
        text: 'hsl(var(--warning))',
        x: X_CUT,
        y: Y_CUT,
        width: PAD_LEFT + INNER_WIDTH - X_CUT,
        height: PAD_TOP + INNER_HEIGHT - Y_CUT,
        labelX: PAD_LEFT + INNER_WIDTH - 8,
        labelY: PAD_TOP + INNER_HEIGHT - 6,
        anchor: 'end',
    },
] as const;

/** Lo que dice el punto al pasar por encima: nombre, cuadrante y las cuatro dimensiones. */
function pointSummary(point: StoreCohortPoint): string {
    const zone = point.quadrant === null ? '' : ` · ${quadrantLabel(point.quadrant)}`;
    return [
        `${scatterStoreLabel(point)}${zone}`,
        `Volumen SK ${formatUnits(point.sureKeepUnits)}`,
        `Captura ${formatPct(point.capturePct)}`,
        `SCI prom. ${point.sciAvg === null ? '—' : formatSci(point.sciAvg)}`,
        `1ª cuota 14d ${formatPct(point.cohortPct)}`,
    ].join(' · ');
}

export function OriginationStoreScatter({
    points,
    selectedStoreId,
    onSelectStore,
}: {
    points: StoreCohortPoint[];
    selectedStoreId: string | null;
    onSelectStore: (storeId: string) => void;
}) {
    const plotted = plottableStorePoints(points);
    const maxUnits = maxSureKeepUnits(plotted);
    // Un punto sin sus dos ejes no se dibuja. Se dice cuántos quedaron fuera: el hueco sin
    // explicación se lee como que la ventana tuvo menos tiendas de las que tuvo.
    const dropped = points.length - plotted.length;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <svg
                viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
                preserveAspectRatio="none"
                className="block min-h-0 w-full flex-1"
                role="img"
                aria-label={`Scatter de ${String(plotted.length)} ${plotted.length === 1 ? 'tienda' : 'tiendas'}: SCI promedio contra la cosecha de 1ª cuota a 14 días, partido en los cuadrantes SUERTE, MODELO, PROBLEMA y ALGO PASA`}
            >
                {QUADRANT_ZONES.map((zone) => (
                    <rect
                        key={zone.quadrant}
                        x={zone.x}
                        y={zone.y}
                        width={zone.width}
                        height={zone.height}
                        fill={zone.fill}
                    />
                ))}

                <line
                    x1={X_CUT}
                    y1={PAD_TOP}
                    x2={X_CUT}
                    y2={PAD_TOP + INNER_HEIGHT}
                    stroke="hsl(var(--border))"
                    strokeDasharray="4 4"
                    opacity={0.8}
                />
                <line
                    x1={PAD_LEFT}
                    y1={Y_CUT}
                    x2={PAD_LEFT + INNER_WIDTH}
                    y2={Y_CUT}
                    stroke="hsl(var(--border))"
                    strokeDasharray="4 4"
                    opacity={0.8}
                />

                {QUADRANT_ZONES.map((zone) => (
                    <text
                        key={`label-${zone.quadrant}`}
                        x={zone.labelX}
                        y={zone.labelY}
                        textAnchor={zone.anchor}
                        fontSize={10}
                        fontWeight={700}
                        letterSpacing="0.06em"
                        fill={zone.text}
                    >
                        {quadrantLabel(zone.quadrant)}
                        <title>{quadrantMeaning(zone.quadrant)}</title>
                    </text>
                ))}

                {[0, 1, 2, 3, 4].map((step) => (
                    <text
                        key={`y-${String(step)}`}
                        x={PAD_LEFT - 4}
                        y={PAD_TOP + (INNER_HEIGHT * step) / 4 + 3}
                        textAnchor="end"
                        fontSize={10}
                        fill="hsl(var(--muted-foreground))"
                    >
                        {100 - (100 * step) / 4}%
                    </text>
                ))}

                {[0, 1, 2, 3, 4].map((step) => {
                    const sci = SCATTER_SCI_MIN + ((SCATTER_SCI_MAX - SCATTER_SCI_MIN) * step) / 4;
                    return (
                        <text
                            key={`x-${String(step)}`}
                            x={PAD_LEFT + (INNER_WIDTH * step) / 4}
                            y={PAD_TOP + INNER_HEIGHT + 14}
                            textAnchor="middle"
                            fontSize={10}
                            fill="hsl(var(--muted-foreground))"
                        >
                            {Math.round(sci)}
                        </text>
                    );
                })}

                <text
                    x={PAD_LEFT + INNER_WIDTH / 2}
                    y={HEIGHT - 8}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={600}
                    fill="hsl(var(--muted-foreground))"
                >
                    SCI promedio de la tienda →
                </text>
                <text
                    x={12}
                    y={PAD_TOP + INNER_HEIGHT / 2}
                    textAnchor="middle"
                    transform={`rotate(-90 12 ${String(PAD_TOP + INNER_HEIGHT / 2)})`}
                    fontSize={10}
                    fontWeight={600}
                    fill="hsl(var(--muted-foreground))"
                >
                    1ª cuota pagada a 14d ↑
                </text>

                {/* Los grandes se dibujan primero para que no tapen a los chicos. */}
                {plotted.map((point) => {
                    const selected = point.storeId === selectedStoreId;
                    const dimmed = selectedStoreId !== null && !selected;
                    const color = captureColor(point.capturePct);
                    const radius = scatterRadius(point.sureKeepUnits, maxUnits);
                    return (
                        <circle
                            key={point.storeId}
                            cx={xPos(point.sciAvg ?? 0)}
                            cy={yPos(point.cohortPct ?? 0)}
                            r={selected ? radius + 2 : radius}
                            fill={color}
                            fillOpacity={selected ? 0.95 : dimmed ? 0.18 : 0.55}
                            stroke={selected ? 'hsl(var(--foreground))' : color}
                            strokeWidth={selected ? 2.5 : 1.5}
                            className="cursor-pointer"
                            onClick={() => {
                                onSelectStore(point.storeId);
                            }}
                        >
                            <title>{pointSummary(point)}</title>
                        </circle>
                    );
                })}
            </svg>

            <p className="flex-none px-3 pb-1.5 text-[9.5px] leading-[1.35] text-muted-foreground">
                Cortes: SCI {STORE_QUADRANT_SCI_CUT} y cosecha {STORE_QUADRANT_COHORT_CUT_PCT}%. El
                tamaño del punto es el volumen SureKeep y el color, la captura.
                {dropped > 0
                    ? ` ${formatUnits(dropped)} ${dropped === 1 ? 'tienda quedó fuera' : 'tiendas quedaron fuera'}: sin SCI o sin cosecha no hay dónde ubicarlas.`
                    : ''}
            </p>
        </div>
    );
}
